import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { lazy, Suspense, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useI18n } from '../i18n';
import type { CalendarSelection } from './calendar';
import {
  dateLocale,
  datePattern,
  formatDate,
  formatDayMonth,
  joinTime,
  parseDate,
  parseDayMonth,
  splitDateTime,
  timeParts,
  type DateLocale,
} from './dates-format';
import { Chrome, TextField, useFieldMessage } from './fields';
import { Icon } from './icon';
import { useCompact } from './index';

// Material's date and time fields (TF-92): an outlined text field one can type the date into, in the
// language chosen in DoCuLat, and a calendar button that opens the date picker - docked under the
// field on a wide window, a modal dialog on a phone. The values are those of the native inputs they
// replace: "2026-10-05", "14:30", "2026-10-05T14:30", so the forms around them did not change.
//
//   DateField       a day
//   DateRangeField  from and to, two fields and one picker (filters)
//   TimeField       hours and minutes, Material's time input (24-hour in Latvian)
//   DateTimeField   a day and its time (due dates, call times)
//   DayMonthField   a day and month without a year (a birthday)

const Calendar = lazy(() => import('./calendar'));

type Translate = ReturnType<typeof useI18n>['t'];

function useLocale(): { locale: DateLocale; t: Translate; pattern: string } {
  const { t, language } = useI18n();
  const locale = useMemo(() => dateLocale(language), [language]);
  return { locale, t, pattern: datePattern(locale, { d: t('dates.d'), m: t('dates.m'), y: t('dates.y') }) };
}

interface CommonProps {
  required?: boolean;
  disabled?: boolean;
  dense?: boolean;
  hint?: ReactNode;
  hideLabel?: boolean;
  className?: string;
  error?: string | null;
}

// ------------------------------------------------------------------------------------- picker

/**
 * The picker: the calendar docked under its field (a popover) on a wide window, Material's modal date
 * picker on a phone (the choice is kept on OK). `children` is the field, given the button that opens it.
 */
function Picker({
  title,
  selection,
  onApply,
  locale,
  min,
  max,
  withoutYear,
  headline,
  disabled,
  children,
}: {
  title: string;
  selection: CalendarSelection;
  onApply: (selection: CalendarSelection) => void;
  locale: DateLocale;
  min?: string;
  max?: string;
  withoutYear?: boolean;
  /** The choice in words, at the top of the modal picker. */
  headline: (selection: CalendarSelection) => string;
  disabled?: boolean;
  children: (button: ReactNode) => ReactNode;
}) {
  const { t } = useI18n();
  const compact = useCompact();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(selection);
  // A range is chosen with two clicks: the first is its start.
  const clicks = useRef(0);
  const opened = (next: boolean) => {
    if (next) {
      setDraft(selection);
      clicks.current = 0;
    }
    setOpen(next);
  };
  const picked = (day: string) => {
    let next: CalendarSelection = { mode: 'single', day };
    let done = true;
    if (selection.mode === 'range') {
      // The first click starts the period, the second ends it (either way round).
      done = clicks.current % 2 === 1;
      const start = draft.mode === 'range' ? draft.from : '';
      next = done ? { mode: 'range', from: start < day ? start : day, to: start < day ? day : start } : { mode: 'range', from: day, to: '' };
    }
    clicks.current += 1;
    setDraft(next);
    if (compact || !done) return;
    onApply(next);
    setOpen(false);
  };
  const calendar = (
    <Suspense fallback={<div className="dp dp-loading" aria-busy="true" />}>
      <Calendar selection={compact ? draft : open ? draft : selection} onSelect={picked} locale={locale} min={min} max={max} withoutYear={withoutYear} />
    </Suspense>
  );
  const button = (
    <button
      type="button"
      className="icon-btn"
      aria-label={t('dates.openCalendar')}
      aria-haspopup="dialog"
      aria-expanded={open}
      disabled={disabled}
      // Docked, the popover's trigger opens it; on a phone, the dialog is opened here.
      onClick={compact ? () => opened(true) : undefined}
    >
      <Icon name="calendar_month" />
    </button>
  );

  if (compact) {
    return (
      <>
        {children(button)}
        <DialogPrimitive.Root open={open} onOpenChange={opened}>
          <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay className="dialog-overlay" />
            <DialogPrimitive.Content className="dialog date-dialog">
              <div className="date-dialog-head">
                <DialogPrimitive.Title className="date-dialog-title">{title}</DialogPrimitive.Title>
                <DialogPrimitive.Description className="date-dialog-headline">{headline(draft)}</DialogPrimitive.Description>
              </div>
              {calendar}
              <div className="dialog-foot">
                <DialogPrimitive.Close className="btn btn-text">{t('common.cancel')}</DialogPrimitive.Close>
                <button
                  type="button"
                  className="btn btn-text"
                  onClick={() => {
                    onApply(draft);
                    setOpen(false);
                  }}
                >
                  {t('dates.ok')}
                </button>
              </div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
      </>
    );
  }
  // Docked under the calendar button, its end at the field's end.
  return (
    <PopoverPrimitive.Root open={open} onOpenChange={opened}>
      {children(<PopoverPrimitive.Trigger asChild>{button}</PopoverPrimitive.Trigger>)}
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content className="popover date-popover" align="end" sideOffset={4} collisionPadding={8} aria-label={title}>
          {calendar}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

// ------------------------------------------------------------------------------- typed dates

/**
 * A day being typed: the text as written, kept apart from the value. The value changes when the text
 * is a whole day (its year typed in full, or on leaving the field); a new value from outside, or
 * another language, rewrites the text.
 */
function useDateText(value: string, onChange: (value: string) => void, locale: DateLocale, check: (day: string | null) => string) {
  const [text, setText] = useState(() => formatDate(value, locale));
  const emitted = useRef(value);
  const written = useRef(locale);
  useEffect(() => {
    if (value === emitted.current && written.current === locale) return;
    emitted.current = value;
    written.current = locale;
    setText(formatDate(value, locale));
  }, [value, locale]);
  const emit = (day: string) => {
    if (day === emitted.current) return;
    emitted.current = day;
    onChange(day);
  };
  return {
    text,
    validate: (typed: string) => check(parseDate(typed, locale)),
    type: (typed: string) => {
      setText(typed);
      const day = parseDate(typed, locale);
      if (day === '' || (day !== null && /\d{4}/.test(typed) && !check(day))) emit(day);
    },
    blur: () => {
      const day = parseDate(text, locale);
      if (day === null || check(day)) return;
      emit(day);
      setText(formatDate(day, locale));
    },
    choose: (day: string) => {
      emit(day);
      setText(formatDate(day, locale));
    },
    /** Shows a day the caller sends on itself (both ends of a range at once). */
    show: (day: string) => {
      emitted.current = day;
      setText(formatDate(day, locale));
    },
  };
}

/** What is wrong with a typed day, in words: not a day, or outside min and max. */
function dayCheck(t: Translate, locale: DateLocale, pattern: string, min?: string, max?: string) {
  return (day: string | null) => {
    if (day === null) return t('dates.notADate', { pattern });
    if (day && min && day < min) return t('dates.notBefore', { date: formatDate(min, locale) });
    if (day && max && day > max) return t('dates.notAfter', { date: formatDate(max, locale) });
    return '';
  };
}

const longDate = (day: string, locale: DateLocale) =>
  day ? new Intl.DateTimeFormat(locale.tag, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(`${day}T12:00:00`)) : '';

// ---------------------------------------------------------------------------------- DateField

export interface DateFieldProps extends CommonProps {
  label: string;
  /** "2026-10-05", or "" for none. */
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  name?: string;
  id?: string;
  autoFocus?: boolean;
  /** A time typed or pasted with the day ("2026-10-05 14:30"): the date and time field takes it. */
  onTime?: (time: string) => void;
}

export function DateField({ label, value, onChange, min, max, name, id, autoFocus, onTime, required, disabled, dense, hint, hideLabel, className, error }: DateFieldProps) {
  const { locale, t, pattern } = useLocale();
  const day = useDateText(value, onChange, locale, dayCheck(t, locale, pattern, min, max));
  return (
    <Picker
      title={t('dates.chooseDate')}
      selection={{ mode: 'single', day: value }}
      onApply={(selection) => selection.mode === 'single' && day.choose(selection.day)}
      locale={locale}
      min={min}
      max={max}
      headline={(selection) => (selection.mode === 'single' && selection.day ? longDate(selection.day, locale) : t('dates.noDate'))}
      disabled={disabled}
    >
      {(button) => (
        <TextField
          id={id}
          name={name}
          label={label}
          value={day.text}
          onChange={(typed) => {
            // The time first, so the day that follows goes out with it.
            const time = /[T\s](\d{1,2}:\d{2})\s*$/.exec(typed.trim())?.[1];
            if (time && onTime && parseDate(typed, locale)) onTime(time.padStart(5, '0'));
            day.type(typed);
          }}
          onBlur={day.blur}
          validate={day.validate}
          placeholder={pattern}
          autoComplete="off"
          autoFocus={autoFocus}
          required={required}
          disabled={disabled}
          dense={dense}
          hint={hint}
          hideLabel={hideLabel}
          className={className}
          error={error}
          trailing={button}
          keepTrailing
        />
      )}
    </Picker>
  );
}

// ----------------------------------------------------------------------------- DateRangeField

export interface DateRange {
  from: string;
  to: string;
}

/** From and to: two fields side by side, and one picker that chooses both (a filter's period). */
export function DateRangeField({
  fromLabel,
  toLabel,
  value,
  onChange,
  min,
  max,
  id,
  dense,
  disabled,
  className,
  checkOrder = true,
}: {
  fromLabel: string;
  toLabel: string;
  value: DateRange;
  onChange: (value: DateRange) => void;
  min?: string;
  max?: string;
  id?: string;
  dense?: boolean;
  disabled?: boolean;
  className?: string;
  /** Refuse an end before the start at the end's field; false where the caller says it in its own words. */
  checkOrder?: boolean;
}) {
  const { locale, t, pattern } = useLocale();
  const autoId = useId();
  const base = id ?? autoId;
  // The latest range, so a change to one end keeps the other as it now is.
  const latest = useRef(value);
  latest.current = value;
  const from = useDateText(value.from, (next) => onChange({ ...latest.current, from: next }), locale, dayCheck(t, locale, pattern, min, max));
  const toCheck = dayCheck(t, locale, pattern, min, max);
  const to = useDateText(value.to, (next) => onChange({ ...latest.current, to: next }), locale, (day) => toCheck(day) || (checkOrder && day && value.from && day < value.from ? t('dates.endBeforeStart') : ''));
  return (
    <Picker
      title={t('dates.chooseDates')}
      selection={{ mode: 'range', from: value.from, to: value.to }}
      onApply={(selection) => {
        if (selection.mode !== 'range') return;
        const range = { from: selection.from, to: selection.to || selection.from };
        from.show(range.from);
        to.show(range.to);
        onChange(range);
      }}
      locale={locale}
      min={min}
      max={max}
      headline={(selection) =>
        selection.mode === 'range' && selection.from ? `${longDate(selection.from, locale)} – ${longDate(selection.to || selection.from, locale)}` : t('dates.noDate')
      }
      disabled={disabled}
    >
      {(button) => (
        <div className={['date-range', className].filter(Boolean).join(' ')}>
          <TextField
            id={`${base}-from`}
            label={fromLabel}
            value={from.text}
            onChange={from.type}
            onBlur={from.blur}
            validate={from.validate}
            placeholder={pattern}
            autoComplete="off"
            dense={dense}
            disabled={disabled}
          />
          <TextField
            id={`${base}-to`}
            label={toLabel}
            value={to.text}
            onChange={to.type}
            onBlur={to.blur}
            validate={to.validate}
            placeholder={pattern}
            autoComplete="off"
            dense={dense}
            disabled={disabled}
            trailing={button}
            keepTrailing
          />
        </div>
      )}
    </Picker>
  );
}

// ---------------------------------------------------------------------------------- TimeField

export interface TimeFieldProps extends CommonProps {
  label: string;
  /** "14:30", or "" for none. */
  value: string;
  onChange: (value: string) => void;
  /** The earliest and latest times ("09:00"), checked like a date's. */
  min?: string;
  max?: string;
  name?: string;
  id?: string;
}

/**
 * Material's time input: hours and minutes, each its own box; a 12-hour locale adds AM and PM. Two
 * digits in the hours move on to the minutes; the up and down arrows count.
 */
export function TimeField({ label, value, onChange, min, max, name, id: givenId, required, disabled, dense, hint, hideLabel, className, error }: TimeFieldProps) {
  const { locale, t } = useLocale();
  const autoId = useId();
  const id = givenId ?? autoId;
  const field = useFieldMessage(name, error);
  const [parts, setParts] = useState(() => timeParts(value, locale));
  const emitted = useRef(value);
  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    setParts(timeParts(value, locale));
  }, [value, locale]);
  const hoursRef = useRef<HTMLInputElement>(null);
  const minutesRef = useRef<HTMLInputElement>(null);
  const check = (next: typeof parts) => {
    const time = joinTime(next.hours, next.minutes, next.pm, locale);
    if (time === null) return t('dates.notATime');
    if (time && min && time < min) return t('dates.notBefore', { date: min });
    if (time && max && time > max) return t('dates.notAfter', { date: max });
    return '';
  };
  const problem = check(parts);
  useLayoutEffect(() => {
    hoursRef.current?.setCustomValidity(problem);
  }, [problem]);
  const change = (next: typeof parts) => {
    setParts(next);
    hoursRef.current?.setCustomValidity(check(next));
    field.onChanged(hoursRef.current);
    const time = joinTime(next.hours, next.minutes, next.pm, locale);
    // A time once both boxes are filled in (or both emptied).
    if (time !== null && !check(next) && (time === '' || next.minutes.length === 2) && time !== emitted.current) {
      emitted.current = time;
      onChange(time);
    }
  };
  const top = locale.hour12 ? 12 : 23;
  const step = (box: 'hours' | 'minutes', by: number, event: KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const limit = box === 'hours' ? top : 59;
    const low = box === 'hours' && locale.hour12 ? 1 : 0;
    const current = Number(parts[box] || (by > 0 ? low - 1 : limit + 1));
    const next = current + by > limit ? low : current + by < low ? limit : current + by;
    change({ ...parts, [box]: box === 'minutes' || !locale.hour12 ? String(next).padStart(2, '0') : String(next), ...(box === 'hours' && !parts.minutes ? { minutes: '00' } : {}) });
  };
  const digits = (text: string) => text.replace(/\D/g, '').slice(0, 2);
  return (
    <Chrome
      id={id}
      chrome={{ label, hint, dense, hideLabel, className: ['tf-time', className].filter(Boolean).join(' ') }}
      required={required}
      disabled={disabled}
      message={field.message}
      up
      kind="input"
      onInvalidCapture={field.onInvalidCapture}
    >
      <input
        ref={hoursRef}
        id={id}
        name={name}
        className="tf-control tf-time-part"
        inputMode="numeric"
        autoComplete="off"
        maxLength={2}
        placeholder={t('dates.hh')}
        aria-label={`${label}, ${t('dates.hours')}`}
        aria-invalid={field.message ? true : undefined}
        aria-describedby={field.message || hint ? `${id}-below` : undefined}
        required={required}
        disabled={disabled}
        value={parts.hours}
        onChange={(event) => {
          const hours = digits(event.currentTarget.value);
          change({ ...parts, hours });
          // Two digits, or one that no second could follow: on to the minutes, ready to be typed over.
          if (hours.length === 2 || Number(hours) > (locale.hour12 ? 1 : 2)) minutesRef.current?.focus();
        }}
        onFocus={(event) => event.currentTarget.select()}
        onKeyDown={(event) => {
          if (event.key === 'ArrowUp') step('hours', 1, event);
          if (event.key === 'ArrowDown') step('hours', -1, event);
        }}
        onBlur={field.onBlur}
      />
      <span className="tf-time-colon" aria-hidden="true">
        :
      </span>
      <input
        ref={minutesRef}
        className="tf-control tf-time-part"
        inputMode="numeric"
        autoComplete="off"
        maxLength={2}
        placeholder={t('dates.mm')}
        aria-label={`${label}, ${t('dates.minutes')}`}
        disabled={disabled}
        value={parts.minutes}
        onChange={(event) => change({ ...parts, minutes: digits(event.currentTarget.value) })}
        onFocus={(event) => event.currentTarget.select()}
        onKeyDown={(event) => {
          if (event.key === 'ArrowUp') step('minutes', 1, event);
          if (event.key === 'ArrowDown') step('minutes', -1, event);
          if (event.key === 'Backspace' && !parts.minutes) hoursRef.current?.focus();
        }}
        onBlur={() => {
          if (parts.minutes.length === 1) change({ ...parts, minutes: `0${parts.minutes}` });
          hoursRef.current?.checkValidity();
        }}
      />
      {locale.hour12 && (
        <button type="button" className="btn btn-text tf-time-period" disabled={disabled} aria-label={parts.pm ? t('dates.pm') : t('dates.am')} onClick={() => change({ ...parts, pm: !parts.pm })}>
          {parts.pm ? t('dates.pm') : t('dates.am')}
        </button>
      )}
    </Chrome>
  );
}

// ------------------------------------------------------------------------------ DateTimeField

export interface DateTimeFieldProps extends CommonProps {
  label: string;
  /** The time box's label; "Time" when not given. */
  timeLabel?: string;
  /** "2026-10-05T14:30" (local time), or "" for none. */
  value: string;
  onChange: (value: string) => void;
  /** The earliest and latest moments ("2026-10-05T14:30"): days outside are disabled, a time outside refused. */
  min?: string;
  max?: string;
  /** The time a day chosen without one gets. */
  defaultTime?: string;
  /**
   * The time may be left empty (TF-220): the value is then the day alone, "2026-10-05" - a task at any
   * time that day. A first day chosen still gets the default time; clearing it makes it a day.
   */
  optionalTime?: boolean;
  /** Beside the time, under it: the zone the time is in (TF-220). */
  trailing?: ReactNode;
  name?: string;
  id?: string;
  autoFocus?: boolean;
}

/** A day and its time, side by side. The value is whole only with a day; the time follows it. */
export function DateTimeField({
  label,
  timeLabel,
  value,
  onChange,
  min,
  max,
  defaultTime = '09:00',
  optionalTime,
  trailing,
  name,
  id,
  autoFocus,
  required,
  disabled,
  dense,
  hint,
  hideLabel,
  className,
  error,
}: DateTimeFieldProps) {
  const { t } = useI18n();
  const autoId = useId();
  const base = id ?? autoId;
  const [parts, setPartsState] = useState(() => splitDateTime(value));
  // The parts as they are now, for a day and a time that arrive in one event (a pasted moment).
  const current = useRef(parts);
  const setParts = (next: { date: string; time: string }) => {
    current.current = next;
    setPartsState(next);
  };
  const emitted = useRef(value);
  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    setParts(splitDateTime(value));
  }, [value]);
  const emit = (next: { date: string; time: string }) => {
    setParts(next);
    const whole = next.date ? (optionalTime && !next.time ? next.date : `${next.date}T${next.time || defaultTime}`) : '';
    if (whole === emitted.current) return;
    emitted.current = whole;
    onChange(whole);
  };
  const bounds = { min: splitDateTime(min ?? ''), max: splitDateTime(max ?? '') };
  return (
    <div className={['date-time', className].filter(Boolean).join(' ')}>
      <DateField
        id={base}
        name={name}
        label={label}
        value={parts.date}
        // A day chosen into an empty field gets the default time; one whose time was cleared stays a day.
        onChange={(date) => emit({ date, time: date && !current.current.time && !(optionalTime && current.current.date) ? defaultTime : current.current.time })}
        // A pasted moment: its time goes out at once (the day may be the one already there).
        onTime={(time) => emit({ ...current.current, time })}
        min={bounds.min.date || undefined}
        max={bounds.max.date || undefined}
        autoFocus={autoFocus}
        required={required}
        disabled={disabled}
        dense={dense}
        hint={hint}
        hideLabel={hideLabel}
        error={error}
      />
      <TimeField
        id={`${base}-time`}
        label={timeLabel ?? t('dates.time')}
        value={parts.time}
        onChange={(time) => emit({ ...current.current, time })}
        min={parts.date && parts.date === bounds.min.date ? bounds.min.time : undefined}
        max={parts.date && parts.date === bounds.max.date ? bounds.max.time : undefined}
        disabled={disabled}
        dense={dense}
      />
      {trailing && <div className="date-time-trailing">{trailing}</div>}
    </div>
  );
}

// ------------------------------------------------------------------------------ DayMonthField

/** A day and a month without a year (a birthday), typed ("12.03") or chosen in a year-less calendar. */
export function DayMonthField({
  label,
  value,
  onChange,
  name,
  id,
  required,
  disabled,
  dense,
  hint,
  hideLabel,
  className,
  error,
}: CommonProps & { label: string; value: { day: string; month: string }; onChange: (value: { day: string; month: string }) => void; name?: string; id?: string }) {
  const { locale, t } = useLocale();
  const pattern = locale.order
    .filter((part) => part !== 'y')
    .map((part) => t(part === 'd' ? 'dates.d' : 'dates.m'))
    .join(locale.separator);
  const [text, setText] = useState(() => formatDayMonth(value.day, value.month, locale));
  const emitted = useRef(`${value.day}.${value.month}`);
  useEffect(() => {
    const key = `${value.day}.${value.month}`;
    if (key === emitted.current) return;
    emitted.current = key;
    setText(formatDayMonth(value.day, value.month, locale));
  }, [value.day, value.month, locale]);
  const emit = (next: { day: string; month: string }) => {
    const key = `${next.day}.${next.month}`;
    if (key === emitted.current) return;
    emitted.current = key;
    onChange(next);
  };
  const asDay = value.day && value.month ? `2000-${value.month.padStart(2, '0')}-${value.day.padStart(2, '0')}` : '';
  return (
    <Picker
      title={t('dates.chooseDay')}
      selection={{ mode: 'single', day: asDay }}
      onApply={(selection) => {
        if (selection.mode !== 'single' || !selection.day) return;
        const next = { day: String(Number(selection.day.slice(8, 10))), month: String(Number(selection.day.slice(5, 7))) };
        emit(next);
        setText(formatDayMonth(next.day, next.month, locale));
      }}
      locale={locale}
      withoutYear
      headline={(selection) =>
        selection.mode === 'single' && selection.day
          ? new Intl.DateTimeFormat(locale.tag, { day: 'numeric', month: 'long' }).format(new Date(`${selection.day}T12:00:00`))
          : t('dates.noDate')
      }
      disabled={disabled}
    >
      {(button) => (
        <TextField
          id={id}
          name={name}
          label={label}
          value={text}
          onChange={(typed) => {
            setText(typed);
            const next = parseDayMonth(typed, locale);
            if (next && (next.day === '' || /\d+\D+\d{1,2}$/.test(typed.trim()))) emit(next);
          }}
          onBlur={() => {
            const next = parseDayMonth(text, locale);
            if (!next) return;
            emit(next);
            setText(formatDayMonth(next.day, next.month, locale));
          }}
          validate={(typed) => (parseDayMonth(typed, locale) ? '' : t('dates.notADay', { pattern }))}
          placeholder={pattern}
          autoComplete="off"
          required={required}
          disabled={disabled}
          dense={dense}
          hint={hint}
          hideLabel={hideLabel}
          className={className}
          error={error}
          trailing={button}
          keepTrailing
        />
      )}
    </Picker>
  );
}
